import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Typography, Button } from "antd";
import { CheckCircleFilled, LeftOutlined, RightOutlined } from "@ant-design/icons";
import heroPageImg from "../../assets/landing-page/hero page.png";

const { Title, Paragraph } = Typography;

// Dynamically load all 300 3D scroll-animation frames (supports .jpg and .png)
const framesContext = require.context(
  "../../assets/landing-page/scroll-animation",
  false,
  /\.(jpg|jpeg|png)$/i
);

const FRAME_KEYS = framesContext.keys().sort((a, b) => {
  const numA = parseInt(a.match(/\d+/)[0], 10);
  const numB = parseInt(b.match(/\d+/)[0], 10);
  return numA - numB;
});

const FRAME_URLS = FRAME_KEYS.map((k) => framesContext(k));

const PERSONA_SLIDES = [
  {
    id: "admin",
    roleTag: "Founders & Sales Leaders",
    headline: "Total Visibility Across the Entire Sales Floor",
    description: "Eliminate guesswork with real-time call telemetry, rep productivity rankings, and automated pipeline risk detection across every lead.",
    features: [
      {
        title: "Hourly Call Density Telemetry",
        desc: "Monitor peak calling hours (9 AM – 7 PM) and inbound vs outbound shares.",
      },
      {
        title: "Ranked Representative Leaderboard",
        desc: "Live rep standings by revenue booked, connected calls, and talk time.",
      },
      {
        title: "Live Team Presence & Break Logs",
        desc: "Real-time WebSocket status tracking for work shifts and break limits.",
      },
    ],
    ctaText: "Start Free Admin Trial",
    altText: "DealVoice Admin Dashboard",
    use3DScroll: true,
  },
  {
    id: "rep",
    roleTag: "Sales Reps & Closers",
    headline: "Zero Admin Drag. Just Focus on Closing.",
    description: "Everything a sales rep needs: automatic call tracking, early stagnation warning radar, and 1-click GST invoices with Razorpay payment links.",
    features: [
      {
        title: "Personalized Target Progress",
        desc: "Real-time progress bar tracking monthly revenue quotas in ₹ INR.",
      },
      {
        title: "7-Day Stuck Deal Warning Radar",
        desc: "Instant alerts flagging stagnant deals before customer interest decays.",
      },
      {
        title: "1-Click Commercial Invoicing",
        desc: "Dispatch quotation PDFs and Razorpay payment links in under 60 seconds.",
      },
    ],
    ctaText: "Start Free Rep Trial",
    altText: "DealVoice Sales Closer Workspace",
    use3DScroll: true,
  },
];

function DualPerspectiveSection({ openTrialSignup }) {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const currentFrameRef = useRef(0);

  // Preload frames for instantaneous 60fps scrubbing
  useEffect(() => {
    const images = [];
    let isMounted = true;

    FRAME_URLS.forEach((url, i) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        if (i === 0 && isMounted) {
          drawFrame(0);
        }
      };
      images[i] = img;
    });

    imagesRef.current = images;

    return () => {
      isMounted = false;
    };
  }, []);

  const drawFrame = (index) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let targetImg = imagesRef.current[index];
    if (!targetImg || !targetImg.complete || targetImg.naturalWidth === 0) {
      // Find closest available loaded frame
      for (let offset = 1; offset < 30; offset++) {
        if (imagesRef.current[index - offset]?.complete) {
          targetImg = imagesRef.current[index - offset];
          break;
        }
        if (imagesRef.current[index + offset]?.complete) {
          targetImg = imagesRef.current[index + offset];
          break;
        }
      }
    }

    if (targetImg && targetImg.complete && targetImg.naturalWidth > 0) {
      if (canvas.width !== targetImg.naturalWidth) {
        canvas.width = targetImg.naturalWidth;
        canvas.height = targetImg.naturalHeight;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Turn light-gray studio backdrop into pure clean white
      ctx.filter = "brightness(1.08) contrast(1.16)";
      ctx.drawImage(targetImg, 0, 0);
      ctx.filter = "none";

      const w = canvas.width;
      const h = canvas.height;

      // Sample clean backdrop color from upper-right studio floor
      const sample = ctx.getImageData(Math.floor(w * 0.96), Math.floor(h * 0.20), 1, 1).data;
      const r = sample[0];
      const g = sample[1];
      const b = sample[2];

      // Cleanly dissolve top-left corner backdrop shade
      const radiusTL = Math.max(w * 0.35, h * 0.45);
      const gradTL = ctx.createRadialGradient(0, 0, 0, 0, 0, radiusTL);
      gradTL.addColorStop(0, `rgb(${r}, ${g}, ${b})`);
      gradTL.addColorStop(0.55, `rgb(${r}, ${g}, ${b})`);
      gradTL.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

      ctx.save();
      ctx.fillStyle = gradTL;
      ctx.beginPath();
      ctx.arc(0, 0, radiusTL, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Cleanly dissolve bottom-right watermark without cutting into the laptop body or shadow
      const cx = w * 0.96;
      const cy = h * 0.84;
      const radius = w * 0.15;

      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      grad.addColorStop(0, `rgb(${r}, ${g}, ${b})`);
      grad.addColorStop(0.55, `rgb(${r}, ${g}, ${b})`);
      grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

      ctx.save();
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  };

  // Sticky Scroll Driver: As the user scrolls through the 250vh section, scrub frames 1..300
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let rafId = null;

    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        const scrollDistance = section.offsetHeight - window.innerHeight;
        if (scrollDistance <= 0) return;

        // Calculate progress from 0 (entry) to 1 (exit)
        const progress = Math.min(1, Math.max(0, -rect.top / scrollDistance));
        setScrollProgress(progress);

        const frameIdx = Math.min(
          FRAME_URLS.length - 1,
          Math.max(0, Math.floor(progress * (FRAME_URLS.length - 1)))
        );

        if (frameIdx !== currentFrameRef.current) {
          currentFrameRef.current = frameIdx;
          drawFrame(frameIdx);
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Re-draw current frame when active slide changes
  useEffect(() => {
    drawFrame(currentFrameRef.current);
  }, [activeSlideIndex]);

  const handlePrev = () => {
    setActiveSlideIndex((prev) => (prev === 0 ? PERSONA_SLIDES.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveSlideIndex((prev) => (prev === PERSONA_SLIDES.length - 1 ? 0 : prev + 1));
  };

  const currentSlide = PERSONA_SLIDES[activeSlideIndex];

  return (
    <section className="dual-perspective-section" id="personas" ref={sectionRef}>
      <div className="dual-sticky-viewport">
        <div className="dual-perspective-container">
          {/* SECTION HEADER */}
          <div className="dual-header-wrap">
            <span className="section-eyebrow-badge">TAILORED FOR EVERY ROLE</span>
            <Title level={2} className="dual-main-title">
              Tailored experiences built <span className="text-highlight-blue">for every sales role</span>
            </Title>
            <Paragraph className="dual-main-subtitle">
              Whether managing a 50-rep sales floor or closing enterprise deals in the field, DealVoice delivers dedicated workflows without compromise.
            </Paragraph>
          </div>

          {/* CAROUSEL WRAPPER WITH FLOATING ARROWS */}
          <div className="persona-carousel-wrapper">
            {/* Left Arrow Button */}
            <button
              type="button"
              className="persona-nav-arrow arrow-left"
              onClick={handlePrev}
              aria-label="Previous persona"
            >
              <LeftOutlined />
            </button>

            {/* MAIN SLIDE CARD */}
            <div className="dual-card-frame">
              <Row gutter={[36, 36]} align="middle" className="dual-content-row">
                {/* Left Column: Feature Highlights */}
                <Col xs={24} lg={11} className="dual-text-col">
                  <div className="dual-text-animator" key={currentSlide.id}>
                    <div className="persona-role-badge">
                      <span>{currentSlide.roleTag}</span>
                    </div>
                    <Title level={3} className="persona-headline">
                      {currentSlide.headline}
                    </Title>
                    <Paragraph className="persona-description">{currentSlide.description}</Paragraph>

                    <div className="persona-feature-list">
                      {currentSlide.features.map((feat, idx) => (
                        <div className="feature-item" key={idx}>
                          <CheckCircleFilled className="feature-check" />
                          <div>
                            <strong>{feat.title}</strong> — {feat.desc}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="persona-cta-wrap">
                      <Button
                        type="primary"
                        size="large"
                        className="persona-action-btn"
                        onClick={() => {
                          if (typeof openTrialSignup === "function") {
                            openTrialSignup("");
                          }
                        }}
                      >
                        {currentSlide.ctaText}
                      </Button>
                    </div>
                  </div>
                </Col>

                {/* Right Column: 3D Scroll Canvas & Visual Presentation */}
                <Col xs={24} lg={13} className="dual-visual-col">
                  <div className="persona-visual-container">
                    <canvas
                      ref={canvasRef}
                      className="persona-scroll-canvas"
                      style={{
                        display: currentSlide.use3DScroll ? "block" : "none",
                        width: "100%",
                        height: "auto",
                      }}
                    />
                    {!currentSlide.use3DScroll && (
                      <img
                        src={heroPageImg}
                        alt={currentSlide.altText}
                        className="persona-fallback-img"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Right Arrow Button */}
            <button
              type="button"
              className="persona-nav-arrow arrow-right"
              onClick={handleNext}
              aria-label="Next persona"
            >
              <RightOutlined />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default DualPerspectiveSection;
